import { sessionQueryOptions } from "@/api-queries/auth";
import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePagination } from "@/hooks/usePagination";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { formatDateWithOptionsBs, formatTimeWithOptionsBs, formatCurrency } from "@/lib/format";
import {
  Calendar,
  Clock,
  User,
  X,
  CircleNotch,
  Warning,
  VideoCamera,
  Sparkle,
  ArrowRight,
} from "@phosphor-icons/react";
import { CLIENT_PAYMENT_STATUS_LABELS } from "@/lib/status-configs";
import type { SessionStatus, PaginatedClientSessions, Session } from "@/types";

const LIMIT = 10;

// ——————————————————————————————————————————————————
// Status theming — each status gets its own color story
// ——————————————————————————————————————————————————

type StatusKey = "scheduled" | "completed" | "cancelled" | "no_show";

interface CancelResult {
  /** Naknada na cijelu seansu (dio firme + dio klijenta). */
  cancelFeeApplied?: number;
  /** Dio naknade koji snosi klijent; kod sufinansiranja manji od cancelFeeApplied. */
  clientFee?: number;
  /** Povrat klijentovog vlastitog novca. Povrat na kredit firme (`companyRefund`)
   *  se klijentu ne prikazuje: zaposleni koji ništa nije platio nema povrat. */
  refundDue?: number;
  companyRefund?: number;
  refundMethod?: "company_credit" | "monri" | "bank_transfer" | null;
}

/**
 * Naknada koju toast pokazuje klijentu. Server vraća i naknadu na cijelu
 * seansu (`cancelFeeApplied`), ali kod sufinansiranja firme klijent je platio
 * samo svoj dio, pa bi mu puna naknada bila veća od uplaćenog iznosa.
 */
export function clientFeeOf(result: CancelResult | null | undefined): number {
  if (typeof result?.clientFee === "number") return result.clientFee;
  return typeof result?.cancelFeeApplied === "number" ? result.cancelFeeApplied : 0;
}

// i18n keys for the status chip label — the STATUS_THEME.label strings stay for
// color/theme lookup but the visible label comes from t() for consistency.
const STATUS_LABEL_KEY: Record<StatusKey, string> = {
  scheduled: "status.scheduled",
  completed: "status.completed",
  cancelled: "status.cancelled",
  no_show: "status.noShow",
};

const STATUS_THEME: Record<
  StatusKey,
  {
    tint: string;
    dot: string;
    textColor: string;
    label: string;
    dateTileBg: string;
    dateTileText: string;
    dateTileAccent: string;
    cardAccentBar: string;
  }
> = {
  scheduled: {
    // Warm brand — this is the active, "live" state; matches site brand
    tint: "oklch(37.12% 0.059 186.62 / 0.12)",
    dot: "oklch(65% 0.15 84)",
    textColor: "oklch(45% 0.13 84)",
    label: "Zakazana",
    dateTileBg:
      "linear-gradient(135deg, oklch(37.12% 0.059 186.62 / 0.18), oklch(49.83% 0.0663 186.62 / 0.14))",
    dateTileText: "oklch(35% 0.12 84)",
    dateTileAccent: "oklch(60% 0.15 84)",
    cardAccentBar:
      "linear-gradient(180deg, oklch(37.12% 0.059 186.62), oklch(29.52% 0.0501 186.62))",
  },
  completed: {
    tint: "oklch(75% 0.13 150 / 0.12)",
    dot: "oklch(60% 0.12 150)",
    textColor: "oklch(40% 0.1 150)",
    label: "Završena",
    dateTileBg:
      "linear-gradient(135deg, oklch(75% 0.13 150 / 0.14), oklch(80% 0.1 140 / 0.1))",
    dateTileText: "oklch(30% 0.09 150)",
    dateTileAccent: "oklch(55% 0.12 150)",
    cardAccentBar:
      "linear-gradient(180deg, oklch(70% 0.13 150), oklch(60% 0.14 145))",
  },
  cancelled: {
    tint: "oklch(70% 0.14 25 / 0.1)",
    dot: "oklch(65% 0.14 25)",
    textColor: "oklch(45% 0.13 25)",
    label: "Otkazana",
    dateTileBg:
      "linear-gradient(135deg, oklch(90% 0.02 30), oklch(93% 0.015 30))",
    dateTileText: "oklch(50% 0.06 30)",
    dateTileAccent: "oklch(62% 0.12 25)",
    cardAccentBar:
      "linear-gradient(180deg, oklch(70% 0.14 25 / 0.6), oklch(65% 0.12 30 / 0.6))",
  },
  no_show: {
    tint: "oklch(80% 0.14 70 / 0.12)",
    dot: "oklch(70% 0.14 70)",
    textColor: "oklch(45% 0.13 70)",
    label: "Nepojavljivanje",
    dateTileBg:
      "linear-gradient(135deg, oklch(80% 0.14 70 / 0.14), oklch(85% 0.1 60 / 0.1))",
    dateTileText: "oklch(40% 0.12 70)",
    dateTileAccent: "oklch(60% 0.14 70)",
    cardAccentBar:
      "linear-gradient(180deg, oklch(75% 0.14 70), oklch(70% 0.13 60))",
  },
};

const PAYMENT_TINT: Record<string, { bg: string; color: string; dot: string }> =
  {
    pending: {
      bg: "oklch(88% 0.12 80 / 0.15)",
      color: "oklch(45% 0.13 80)",
      dot: "oklch(70% 0.14 80)",
    },
    // A hold that still needs the client to finalize + pay. Without this entry
    // the chip stayed hidden and the session looked fully confirmed.
    pending_hold: {
      bg: "oklch(80% 0.14 60 / 0.14)",
      color: "oklch(45% 0.14 55)",
      dot: "oklch(68% 0.15 55)",
    },
    pending_transfer: {
      bg: "oklch(80% 0.14 60 / 0.12)",
      color: "oklch(45% 0.13 60)",
      dot: "oklch(68% 0.14 60)",
    },
    failed: {
      bg: "oklch(70% 0.14 25 / 0.12)",
      color: "oklch(45% 0.13 25)",
      dot: "oklch(65% 0.14 25)",
    },
    refunded: {
      bg: "oklch(75% 0.1 240 / 0.12)",
      color: "oklch(45% 0.12 240)",
      dot: "oklch(60% 0.13 240)",
    },
    paid: {
      bg: "oklch(75% 0.13 150 / 0.12)",
      color: "oklch(40% 0.12 150)",
      dot: "oklch(60% 0.12 150)",
    },
  };

// ——————————————————————————————————————————————————

function formatDateParts(iso: string, locale: string) {
  const date = new Date(iso);
  if (locale === "bs-BA") {
    return {
      day: formatDateWithOptionsBs(iso, { day: "numeric", timeZone: "Europe/Sarajevo" }),
      month: formatDateWithOptionsBs(iso, { month: "short", timeZone: "Europe/Sarajevo" }).replace(/\.$/, ""),
      weekday: formatDateWithOptionsBs(iso, { weekday: "short", timeZone: "Europe/Sarajevo" }),
      fullDate: formatDateWithOptionsBs(iso, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "Europe/Sarajevo",
      }),
      time: formatTimeWithOptionsBs(iso, {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Sarajevo",
      }),
    };
  }

  return {
    day: date.toLocaleDateString(locale, { day: "numeric", timeZone: "Europe/Sarajevo" }),
    month: date
      .toLocaleDateString(locale, { month: "short", timeZone: "Europe/Sarajevo" })
      .replace(/\.$/, ""),
    weekday: date.toLocaleDateString(locale, { weekday: "short", timeZone: "Europe/Sarajevo" }),
    fullDate: date.toLocaleDateString(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "Europe/Sarajevo",
    }),
    time: date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Sarajevo",
    }),
  };
}

export function ClientSessionsPanel() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { data: session } = useQuery(sessionQueryOptions());
  const locale = i18n.language === "en" ? "en-US" : "bs-BA";

  const tabs: { label: string; value: SessionStatus | "all" }[] = [
    { label: t("status.scheduledPlural"), value: "scheduled" },
    { label: t("status.completedPlural"), value: "completed" },
    { label: t("status.cancelledPlural"), value: "cancelled" },
    { label: t("common.all"), value: "all" },
  ];
  const { page, setPage, filters, setFilter, pageSize } = usePagination<{
    activeTab: SessionStatus | "all";
  }>({ pageSize: LIMIT }, { activeTab: "scheduled" });
  const { activeTab } = filters;
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const statusParam = activeTab === "all" ? "" : `&status=${activeTab}`;

  const { data, isLoading, isError, refetch } = useQuery<PaginatedClientSessions>({
    queryKey: ["client", "sessions", session?.user.id, activeTab, page],
    enabled: !!session?.user.id,
    queryFn: async () => {
      const res = await fetch(
        `/api/sessions/my?page=${page}&limit=${pageSize}${statusParam}`
      );
      await throwIfNotOk(res, t("client.sessions.errorLoading"));
      return res.json();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await fetch(`/api/sessions/${sessionId}/cancel`, {
        method: "POST",
      });
      await throwIfNotOk(res, t("client.sessions.errorCancelling"));
      return res.json() as Promise<CancelResult>;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["client", "sessions"] });
      const fee = clientFeeOf(result);
      const refund = typeof result?.refundDue === "number" ? result.refundDue : null;
      let message: string;
      if (fee > 0 && refund != null && refund > 0) {
        message = t("client.sessions.cancelledWithFee", {
          fee: formatCurrency(fee),
          refund: formatCurrency(refund),
        });
      } else if (fee > 0) {
        message = t("client.sessions.cancelledFeeOnly", { fee: formatCurrency(fee) });
      } else if (refund != null && refund > 0) {
        message = t("client.sessions.cancelledRefundOnly", { refund: formatCurrency(refund) });
      } else {
        message = t("client.sessions.cancelSuccess");
      }
      toast.success(message);
      setCancellingId(null);
    },
    onError: (err: Error) => {
      toast.error(err.message);
      setCancellingId(null);
    },
  });

  const sessions = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-6">
      {/* Sub-tabs — pill group matching site aesthetic */}
      <div
        className="inline-flex flex-wrap gap-1 rounded-full p-1"
        style={{
          background: "oklch(98% 0.005 84)",
          border: "1px solid var(--color-warm-border)",
        }}
      >
        {tabs.map((tab) => {
          const active = activeTab === tab.value;
          return (
            <button
              key={tab.value}
              onClick={() => setFilter("activeTab", tab.value)}
              className={cn(
                "rounded-full px-4 py-1.5 text-[0.8rem] font-medium transition-colors",
                active
                  ? "text-[color:var(--color-brand-dark)]"
                  : "text-muted-foreground hover:text-foreground"
              )}
              style={{
                background: active ? "oklch(100% 0 0)" : "transparent",
                boxShadow: active
                  ? "0 1px 4px oklch(0% 0 0 / 0.04)"
                  : undefined,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <CircleNotch
            className="size-7 animate-spin"
            style={{ color: "var(--color-brand)" }}
          />
        </div>
      ) : isError ? (
        <ErrorState onRetry={() => void refetch()} t={t} />
      ) : sessions.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className="space-y-3">
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                locale={locale}
                onCancel={() => setCancellingId(session.id)}
                cancelPending={cancelMutation.isPending}
                t={t}
              />
            ))}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </>
      )}

      {cancellingId && (
        <CancelDialog
          isPending={cancelMutation.isPending}
          onConfirm={() => cancelMutation.mutate(cancellingId)}
          onDismiss={() => setCancellingId(null)}
          t={t}
        />
      )}
    </div>
  );
}

// ————————————————————————————————————————————————————————————————
// Session card — the hero of this panel
// ————————————————————————————————————————————————————————————————

export function SessionCard({
  session,
  locale,
  onCancel,
  cancelPending,
  t,
}: {
  session: Session;
  locale: string;
  onCancel: () => void;
  cancelPending: boolean;
  t: (key: string) => string;
}) {
  const parts = formatDateParts(session.scheduledAt, locale);
  const status = (session.status as StatusKey) ?? "scheduled";
  const theme = STATUS_THEME[status] ?? STATUS_THEME.scheduled;
  const isScheduled = status === "scheduled";
  const isExpired = isScheduled && new Date(session.scheduledAt) < new Date();
  // cancelReason/cancelledBy are on the row (SELECT ts.*) but not in the shared
  // Session type; read defensively.
  const cancelReason = (session as Session & { cancelReason?: string | null }).cancelReason;
  // Emergency sessions carry no service (serviceId is NULL by design), so
  // without this they would render as a generic "Sesije" with nothing telling
  // the client what they booked.
  const isEmergency = session.kind === "emergency";

  return (
    <article
      className="group relative overflow-hidden rounded-2xl bg-white transition-all duration-300 hover:-translate-y-0.5"
      style={{
        border: "1px solid var(--color-warm-border)",
        opacity: isExpired ? 0.55 : undefined,
        filter: isExpired ? "grayscale(0.4)" : undefined,
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.boxShadow =
          "0 8px 24px oklch(37.12% 0.059 186.62 / 0.1)";
        e.currentTarget.style.borderColor = "oklch(88% 0.05 84)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "var(--color-warm-border)";
      }}
    >
      {/* Left accent bar — colored by status */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ background: theme.cardAccentBar }}
      />

      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-stretch sm:gap-5 sm:p-5 sm:pl-6">
        {/* Date tile */}
        <div
          className="flex shrink-0 flex-row items-center gap-3 rounded-xl px-4 py-3 sm:w-[88px] sm:flex-col sm:items-center sm:justify-center sm:gap-0 sm:px-0 sm:py-3"
          style={{
            background: theme.dateTileBg,
            border: `1px solid ${theme.dateTileAccent}33`,
          }}
        >
          <span
            className="text-3xl font-bold leading-none sm:text-[2rem]"
            style={{
              fontFamily: "var(--font-heading)",
              color: theme.dateTileText,
            }}
          >
            {parts.day}
          </span>
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.16em] sm:mt-1"
            style={{ color: theme.dateTileAccent }}
          >
            {parts.month}
          </span>
          <span
            className="text-[10px] font-medium capitalize text-muted-foreground sm:mt-0.5"
            style={{ opacity: 0.8 }}
          >
            {parts.weekday}
          </span>
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1 space-y-2.5">
          {/* Top row: service name + chips */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3
                className="truncate text-base font-semibold leading-tight text-foreground sm:text-lg"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {isEmergency
                  ? t("emergency.sessionTitle")
                  : session.serviceName || t("common.sessions")}
              </h3>
              <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                <User className="size-3.5 shrink-0 opacity-70" />
                <span className="truncate">{session.therapistName}</span>
              </div>
            </div>

            {/* Status chip (+ emergency marker, same convention as the therapist calendar) */}
            <div className="flex shrink-0 flex-col items-end gap-1">
              {isEmergency && (
                <span className="inline-flex items-center rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                  {t("emergency.badge")}
                </span>
              )}
              <StatusChip
                label={isExpired ? t("client.sessions.awaitingConfirmation") : t(STATUS_LABEL_KEY[status])}
                dot={isExpired ? "oklch(60% 0.03 250)" : theme.dot}
                tint={isExpired ? "oklch(85% 0.02 250 / 0.15)" : theme.tint}
                color={isExpired ? "oklch(45% 0.03 250)" : theme.textColor}
              />
            </div>
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.8rem] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-3.5 opacity-70" />
              <span>{parts.time}</span>
            </span>
            <Dot />
            <span>{session.durationMinutes} min</span>
            <Dot />
            <span>{session.modality === "in_person" ? t("booking.modalityInPerson") : t("booking.modalityOnline")}</span>
            <Dot />
            <span className="hidden sm:inline">{parts.fullDate}</span>
            <span className="sm:hidden">
              {parts.weekday}, {parts.day} {parts.month}
            </span>
            {session.paymentStatus && session.paymentStatus !== "paid" && (
              <PaymentChip status={session.paymentStatus} />
            )}
          </div>

          {/* Cancellation reason — only surfaced on the card for cancelled sessions */}
          {status === "cancelled" && cancelReason && (
            <p className="text-[0.8rem] text-muted-foreground">
              <span className="font-medium text-foreground">{t("client.sessions.cancelReasonLabel")}:</span>{" "}
              {cancelReason}
            </p>
          )}

          {/* Actions — only scheduled sessions that haven't passed */}
          {isScheduled && !isExpired && (
            <div className="flex flex-wrap gap-2 pt-1.5">
              {session.modality === "in_person" && session.officeAddress && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-medium text-muted-foreground"
                  style={{ borderColor: "oklch(92% 0.02 25)" }}
                >
                  {t("booking.modalityLocation")}: {session.officeAddress}
                </span>
              )}
              {session.meetLink && (
                <a
                  href={session.meetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/btn inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white transition-all duration-200 hover:scale-[1.03]"
                  style={{
                    background:
                      "linear-gradient(135deg, oklch(37.12% 0.059 186.62), oklch(29.52% 0.0501 186.62))",
                    boxShadow: "0 2px 10px oklch(37.12% 0.059 186.62 / 0.25)",
                  }}
                >
                  <VideoCamera className="size-3.5" />
                  {t("client.sessions.joinMeet")}
                  <ArrowRight className="size-3 opacity-0 transition-all duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:opacity-100" />
                </a>
              )}
              <button
                onClick={onCancel}
                disabled={cancelPending}
                className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-medium transition-colors disabled:opacity-50"
                style={{
                  borderColor: "oklch(92% 0.02 25)",
                  color: "oklch(55% 0.13 25)",
                  background: "oklch(100% 0 0)",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "oklch(97% 0.015 25)";
                  e.currentTarget.style.borderColor = "oklch(86% 0.05 25)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "oklch(100% 0 0)";
                  e.currentTarget.style.borderColor = "oklch(92% 0.02 25)";
                }}
              >
                <X className="size-3.5" />
                {t("client.sessions.cancelSession")}
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function StatusChip({
  label,
  dot,
  tint,
  color,
}: {
  label: string;
  dot: string;
  tint: string;
  color: string;
}) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.08em]"
      style={{ background: tint, color }}
    >
      <span
        className="inline-block size-1.5 rounded-full"
        style={{ background: dot }}
      />
      {label}
    </span>
  );
}

function PaymentChip({ status }: { status: string }) {
  const theme = PAYMENT_TINT[status];
  const label = CLIENT_PAYMENT_STATUS_LABELS[status] ?? status;
  if (!theme) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-medium"
      style={{ background: theme.bg, color: theme.color }}
    >
      <span
        className="inline-block size-1 rounded-full"
        style={{ background: theme.dot }}
      />
      {label}
    </span>
  );
}

function Dot() {
  return (
    <span
      aria-hidden
      className="hidden h-1 w-1 rounded-full bg-muted-foreground/40 sm:inline-block"
    />
  );
}

// ————————————————————————————————————————————————————————————————
// Empty state
// ————————————————————————————————————————————————————————————————

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div
      className="relative overflow-hidden rounded-3xl bg-white px-6 py-14 text-center sm:py-16"
      style={{ border: "1px solid var(--color-warm-border)" }}
    >
      {/* Decorative orb */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full opacity-40 blur-3xl"
        style={{ background: "var(--color-brand-bg)" }}
      />

      <div className="relative">
        <div
          className="mx-auto mb-5 flex size-14 items-center justify-center rounded-2xl"
          style={{
            background: "var(--color-brand-bg-light)",
            border: "1px solid var(--color-brand-bg)",
          }}
        >
          <Sparkle
            className="size-6"
            style={{ color: "var(--color-brand-dark)" }}
          />
        </div>
        <h3
          className="text-lg font-semibold tracking-tight text-foreground sm:text-xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {t("client.sessions.noSessions")}
        </h3>
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
          {t("client.sessions.browseTherapists")}
        </p>
        <Link to="/therapists" className="inline-block">
          <span
            className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:scale-[1.03]"
            style={{
              background:
                "linear-gradient(135deg, oklch(37.12% 0.059 186.62), oklch(29.52% 0.0501 186.62))",
              boxShadow: "0 2px 14px oklch(37.12% 0.059 186.62 / 0.3)",
            }}
          >
            {t("client.sessions.viewTherapists")}
            <ArrowRight className="size-4" />
          </span>
        </Link>
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————————————————
// Error state — distinct from the empty state
// ————————————————————————————————————————————————————————————————

function ErrorState({ onRetry, t }: { onRetry: () => void; t: (key: string) => string }) {
  return (
    <div
      className="rounded-3xl bg-white px-6 py-14 text-center sm:py-16"
      style={{ border: "1px solid var(--color-warm-border)" }}
    >
      <div
        className="mx-auto mb-5 flex size-14 items-center justify-center rounded-2xl"
        style={{ background: "oklch(96% 0.025 25)", border: "1px solid oklch(90% 0.04 25)" }}
      >
        <Warning className="size-6" style={{ color: "oklch(55% 0.15 25)" }} />
      </div>
      <p className="text-sm text-muted-foreground">{t("client.sessions.loadError")}</p>
      <Button variant="outline" size="sm" className="mt-4 rounded-full" onClick={onRetry}>
        {t("client.sessions.retry")}
      </Button>
    </div>
  );
}

// ————————————————————————————————————————————————————————————————
// Cancel dialog
// ————————————————————————————————————————————————————————————————

function CancelDialog({
  isPending,
  onConfirm,
  onDismiss,
  t,
}: {
  isPending: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  t: (key: string) => string;
}) {
  const dialog = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: "oklch(0% 0 0 / 0.35)",
        backdropFilter: "blur(6px)",
      }}
      onClick={onDismiss}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-3xl bg-white"
        style={{
          border: "1px solid var(--color-warm-border)",
          boxShadow: "0 24px 60px oklch(0% 0 0 / 0.16)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div
              className="flex size-11 shrink-0 items-center justify-center rounded-2xl"
              style={{
                background: "oklch(96% 0.025 25)",
                border: "1px solid oklch(90% 0.04 25)",
              }}
            >
              <Warning
                className="size-5"
                style={{ color: "oklch(55% 0.15 25)" }}
              />
            </div>
            <div>
              <h3
                className="text-lg font-semibold tracking-tight text-foreground"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {t("client.sessions.cancelSessionTitle")}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("client.sessions.cancelConfirm")}
              </p>
              <p className="mt-3 inline-flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                <Warning className="size-3.5 shrink-0 mt-0.5" />
                <span>{t("client.sessions.cancelWarning")}</span>
              </p>
            </div>
          </div>
        </div>
        <div
          className="flex items-center justify-end gap-2 border-t px-6 py-4"
          style={{
            borderColor: "var(--color-warm-border)",
            background: "oklch(98% 0.005 84 / 0.5)",
          }}
        >
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            {t("common.back")}
          </Button>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-full"
            style={{
              background: "oklch(60% 0.17 25)",
              color: "white",
            }}
          >
            {isPending ? (
              <CircleNotch className="size-4 animate-spin" />
            ) : (
              <X className="size-4" />
            )}
            {t("client.sessions.yesCancel")}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
